import {h} from 'preact';
import {Formik} from 'formik';
import Notifier from '@modules/ui/Notifier';
import {TextField} from '@components/common/Form/TextField';
import {Address} from '@components/common/Address';
import Button from '@components/common/Button';
import {trimFormData} from '@util/StringUtils';
import {getGraphQLErrorsByPathName} from '@modules/core/GraphQLProvider/util';
import {GraphQLErrorDisplay} from '@components/common/GraphQLError';
import Message from '@components/common/Message';
import {FormFeedback} from '@components/common/Form';
import {SmsMutations} from '@services/SmsService/mutations';
import {VendorLabel} from '@containers/SmsPortal/components';
import {PageContent, BackButton, NumberedHeading, CheckoutLineItem} from '../components';
import reject from 'ramda/src/reject';
import not from 'ramda/src/not';
import indexBy from 'ramda/src/indexBy';
import prop from 'ramda/src/prop';
import pathOr from 'ramda/src/pathOr';
import {
    groupByVendorAndPlaceId,
    toContactInfo,
    mapShipmentGroupsToRma,
    stitchRmaRequestToResponse
} from '../helpers';
import Validator from '@util/Validator';
import appLogger from '../../../modules/core/LoggingProvider';

const cartFormValidator = new Validator( {
    contactFullName: {
        alias: 'Full name',
        required: true,
        fullName: true

    },
    email: {
        alias: 'Email address',
        required: true,
        email: true
    },
    phone: {
        alias: 'Phone number',
        required: true,
        phoneNumber: {locale: 'en-US'}
    }
} );

// todo -> move to GraphQL Utility
function getIneligibleItemsFromError ( apolloError ) {
    if ( !apolloError ) {
        return {};
    }
    const errors = getGraphQLErrorsByPathName( 'createRmaRequest', apolloError );
    // todo -> convert code below to util method that can extract GraphQL err extensions based on code
    //  this could be handled from a higher-order function or switch statement
    const rmaItems = errors
        .reduce( ( acc, err ) => {
            const errExt = pathOr( {}, ['extensions'], err );
            if ( errExt.code === 'SmsRmaAlreadyOpenForItems' ) {
                acc = acc.concat( pathOr( [], ['data', 'rmaLineItems'], errExt ) );
            }
            return acc;
        }, [] )
        .map( item => ({
            serialId: item.SerialId,
            requestId: item.RequestId
        }) );

    return indexBy( prop( 'serialId' ), rmaItems );
}

export const ContactFormFields = ( {values, errors, touched, handleSubmit, handleChange} ) =>
    <fieldset data-testid="contact-fieldset" className="mt-0 md:max-w-xs">
        <TextField
            id="contactFullName"
            label="Full name*"
            onChange={handleChange}
            error={touched.contactFullName && errors.contactFullName}
            value={values.contactFullName}
            inputClassName="w-full"
            className="mt-2"/> <TextField
        id="email"
        label="Email address*"
        onChange={handleChange}
        error={touched.email && errors.email}
        value={values.email}
        inputClassName="w-full"
        className="mt-2"/> <TextField
        id="phone"
        label="Phone number*"
        onChange={handleChange}
        error={touched.phone && errors.phone}
        value={values.phone}
        inputClassName="w-full"
        className="mt-2"/>
    </fieldset>;

export const RepairsCheckout = ( props, context ) => {
    const groupedShipments = groupByVendorAndPlaceId( props.list );

    return (
        <SmsMutations render={( {createRmaRequest} ) => {
            // todo -> consider moving this to <GraphQLErrorDisplay>
            if ( createRmaRequest.result.error &&
                createRmaRequest.result.error.networkError ) {
                Notifier.error(
                    'This could be due to a problem on our end or a poor internet connection. Please try again.',
                    'Sorry, it looks like this request did not complete'
                );
            }

            return (
                <Formik
                    initialValues={{
                        contactFullName: '',
                        email: '',
                        phone: ''
                    }}
                    validate={formData => {
                        const trimmedFormData = trimFormData( formData );
                        const errors = cartFormValidator.validate( trimmedFormData );
                        return reject( not, errors );
                    }}
                    onSubmit={( values, formikBag ) => {
                        // format an RMA request from the grouped shipments to conform to the RmaInput input type
                        const contactInfo = toContactInfo( trimFormData( values ) );
                        const smsRmas = mapShipmentGroupsToRma( groupedShipments, contactInfo );

                        createRmaRequest
                            .mutation( {variables: {input: {smsRmas}}} )
                            .then( ( {data} ) => {
                                const stitchedRmaResponse = stitchRmaRequestToResponse( {
                                    groupedShipments,
                                    contactInfo,
                                    addresses: context.customerAddresses,
                                    rmaResponse: data.createRmaRequest
                                } );

                                props.history.push( {
                                    pathname: '/checkout/confirmation',
                                    state: {
                                        rmas: stitchedRmaResponse
                                    }
                                } );

                                props.actions.clearCart();
                            } )
                            .catch( err => {
                                appLogger.capture( err );
                            } )
                            .finally( formikBag.setSubmitting( false ) );
                    }}
                    render={formikBag => {
                        const ineligibleItems = getIneligibleItemsFromError( createRmaRequest.result.error );
                        return (
                            <form className="mt-2" onSubmit={formikBag.handleSubmit}>
                                <BackButton className="mb-8" history={props.history}/>
                                <PageContent>
                                    <div className="max-w-xl">
                                        <NumberedHeading step="1">Contact information</NumberedHeading>
                                        <p className="mt-2 md:w-2/3">Your contact information is needed so a qualified
                                            member of our Equipment Service Team can communicate with you throughout the
                                            repairs process.</p>
                                        <small className="block mt-4">*Indicates a required field</small>
                                        <ContactFormFields {...formikBag}/>
                                    </div>
                                    <NumberedHeading className="mt-8" step="2"> Review equipment and verify your
                                        shipments </NumberedHeading> {Object.entries( groupedShipments )
                                    .map( ( [key, items], index, arr ) =>
                                        <div key={`${key}-${index}`}
                                             data-testid="checkout-shipment-group"
                                             className="mt-4 p-4 lg:flex shadow-1 rounded">
                                            <header className="w-64">
                                                <h2 className="text-lg">
                                                    Shipment {index + 1} of {arr.length}
                                                </h2>
                                                <div className="mt-4">
                                                    <p>Repairs will be fulfilled by <VendorLabel name={items[0].vendor}
                                                                                                 className="text-lg block"/>
                                                    </p>
                                                </div>
                                                <div className="mt-4">
                                                    <h3>Shipping from</h3>
                                                    <Address {...items[0].address} />
                                                </div>
                                            </header>

                                            <div className="lg:ml-16 flex-1">
                                                {items.map( item =>
                                                    <CheckoutLineItem {...item}
                                                                      openRma={ineligibleItems[item.serialId]}
                                                                      onRemoveItem={props.actions.removeItem}/>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <GraphQLErrorDisplay
                                        error={createRmaRequest.result.error}
                                        render={graphQLErrors =>
                                            <Message data-testid="error-message" fail className="mt-8 max-w-md mx-auto">
                                                {graphQLErrors.map( ( {message} ) =>
                                                    <span>{message}</span>
                                                )}
                                            </Message>
                                        }/>

                                    <FormFeedback className="max-w-md mx-auto" {...formikBag} />
                                    <div className="mt-12 button-bar mx-auto w-full max-w-md">
                                        <Button tabIndex="0"
                                                primary
                                                type="submit"
                                                className="max-w-xs"
                                                disabled={formikBag.isSubmitting}
                                                loading={createRmaRequest.result.loading}> Submit repair
                                            request </Button>
                                    </div>
                                </PageContent>
                            </form>
                        );
                    }}
                />
            );
        }}/>
    );
};